import React from 'react';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { upgradeHref } from './design-system/plan/plan-limits';

interface UpgradePromptProps {
  title: string;
  description: string;
  className?: string;
  /** The limit or feature behind it, so the upgrade page opens on it. */
  reason?: string;
}

/**
 * The card a vendor meets when their plan stops them.
 *
 * `FeatureGate` (the plan does not include this at all) and
 * `LimitReachedGate` (the plan includes it but the quota is spent) are
 * different questions with the same answer, and they had drifted into two
 * copies of the same forty lines. One card, one wording, one CTA.
 */
export const UpgradePrompt: React.FC<UpgradePromptProps> = ({ title, description, className, reason }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={cn('rounded-xl border bg-card p-6 text-center md:p-8', className)}>
      <span className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-muted">
        <Lock className="size-6 text-muted-foreground" />
      </span>
      <h2 className="font-heading mb-2 text-xl font-medium tracking-tight">{title}</h2>
      <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{description}</p>
      <Button variant="outline" onClick={() => navigate(upgradeHref(reason))}>
        {t('PLANS.LOCKED.UPGRADE_CTA', 'View plans')}
      </Button>
    </div>
  );
};

export default UpgradePrompt;
